/**
 * 🔒 A list's built-in views and its quick views (piece 13; decision 2880).
 *
 * A built-in view is declared in the list register beside the columns: a name,
 * and what the list's read already takes, either the read's own parameters
 * (the Companies list's `role`, the Lead list's `status` and `unrouted`, the
 * Items list's `offDefaultAccounts`) or condition rows on the saved-view path
 * (a status, or the statuses an Open or Closed view leaves out). It is never
 * edited or deleted, and it reads through the same criteria path a person's own
 * view does.
 *
 * A quick view is a view a person stars, built-in or their own. The starred
 * views stand in the bar above the list, the first three always and the fourth
 * and fifth where the bar holds them, and lead the switcher (decision 2171).
 */
import type { CriteriaRow, ViewComparator } from '../modals/newViewModal';
import { statusDefinitionSeed } from '../statusDefinitions';
import { ALL_VIEW_ID } from '../shell/savedView';

/** One condition a built-in view reads by, on the saved-view path (decision 2172's comparators). */
export interface BuiltInCriterion {
  field: string;
  comparator: ViewComparator;
  value: string;
}

export interface BuiltInView {
  /** The word the address carries as `savedView`, and the key a person's star row names. All is `ALL_VIEW_ID`. */
  key: string;
  /** What the switcher and the bar read. */
  name: string;
  /** The read's own parameters, such as `role=customer` on the Companies list. */
  filter?: Readonly<Record<string, string>>;
  /** Condition rows on the saved-view path, such as a status. */
  criteria?: readonly BuiltInCriterion[];
  /** 🔒 Starred for every person until they unstar it (decision 2880). */
  starred?: boolean;
}

/** 🔒 A person's star on a built-in view rides in their own row's conditions under a field no column has, as the Tags filter does (decision 2880). */
export const BUILT_IN_VIEW_CRITERION = '@builtInView';
/** 🔒 The most views a list's bar holds, and so the most a person stars on one list (decision 2880). */
export const QUICK_VIEW_LIMIT = 5;
/** 🔒 The bar always draws the first three starred views; the fourth and fifth draw where its width holds them. */
export const QUICK_VIEWS_ALWAYS = 3;

/** The All view, which every list carries first. */
export function allView(name: string, starred = true): BuiltInView {
  return { key: ALL_VIEW_ID, name, starred };
}

/**
 * 🔒 One view per member of a status enum, in the status table's own order and
 * under its own label, so no status is named by hand (decision 2880).
 */
export function statusViews(entityType: string, field = 'status'): BuiltInView[] {
  return statusDefinitionSeed
    .filter((row) => row.entityType === entityType)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((row) => ({ key: `status-${row.statusValue}`, name: row.label, criteria: [{ field, comparator: 'is', value: row.statusValue }] }));
}

/** The members of a status enum, in the table's order. */
export function statusMembers(entityType: string): string[] {
  return statusDefinitionSeed.filter((row) => row.entityType === entityType).sort((a, b) => a.sortOrder - b.sortOrder).map((row) => row.statusValue);
}

/**
 * 🔒 An accounting document list's two combined views (decision 2880): Open
 * leaves out every status the decision names as fully satisfied, and Closed
 * leaves out every other one. Both are condition rows the saved-view path
 * already reads, every row holding at once.
 */
export function openClosedViews(entityType: string, closed: readonly string[], names: { open: string; closed: string }): BuiltInView[] {
  const members = statusMembers(entityType);
  const open = members.filter((member) => !closed.includes(member));
  const leaving = (out: readonly string[]): BuiltInCriterion[] => out.map((value) => ({ field: 'status', comparator: 'isNot', value }));
  return [
    { key: 'open', name: names.open, criteria: leaving(closed), starred: true },
    { key: 'closed', name: names.closed, criteria: leaving(open), starred: true },
  ];
}

/** The rows a built-in view's conditions send on the read, each with an id the server's reader accepts. */
export function builtInCriteria(view: BuiltInView): CriteriaRow[] {
  return (view.criteria ?? []).map((row, index) => ({ id: `${view.key}-${index}`, ...row }));
}

/** Whether a built-in view narrows the list at all; All does not. */
export function builtInNarrows(view: BuiltInView): boolean {
  return (view.criteria?.length ?? 0) > 0 || Object.keys(view.filter ?? {}).length > 0;
}

/** The built-in view a person's star row stands for, or null where the row is a view of their own. */
export function builtInKeyOf(criteria: readonly { field: string; value: string }[] | null | undefined): string | null {
  const row = (criteria ?? []).find((c) => c.field === BUILT_IN_VIEW_CRITERION);
  return row && row.value !== '' ? row.value : null;
}

/** 🔒 The conditions a star row carries: the built-in view's own, then its key under the field no column has (decision 2880). */
export function starRowCriteria(view: BuiltInView): CriteriaRow[] {
  return [...builtInCriteria(view), { id: BUILT_IN_VIEW_CRITERION, field: BUILT_IN_VIEW_CRITERION, comparator: 'is', value: view.key }];
}

/**
 * 🔒 The column keys a built-in view draws (decision 2981): the ones the person
 * arranged on their own star row where they have it, and none otherwise, which
 * draws the list's own set. The conditions are always the register's.
 */
export function builtInColumnKeys(rows: ReadonlyMap<string, { columns: readonly string[] }>, key: string): readonly string[] {
  return rows.get(key)?.columns ?? [];
}

/** One row of the switcher, built-in or the person's own, with its star as it stands for this person. */
export interface SwitcherEntry {
  id: string;
  name: string;
  builtIn: boolean;
  starred: boolean;
}

/**
 * 🔒 The switcher's order (decisions 2171, 2880): All first; then the starred
 * views, built-in in the register's order before the person's own in name
 * order; then the unstarred built-in views in the register's order; then the
 * person's other views as they were made. A built-in view's star is the
 * person's own where they have changed it, and the register's otherwise.
 */
export function switcherEntries(
  builtIns: readonly BuiltInView[],
  own: readonly { id: string; name: string; isFavourite?: boolean }[],
  stars: ReadonlyMap<string, boolean>,
): SwitcherEntry[] {
  const builtIn = builtIns.map((view): SwitcherEntry => ({ id: view.key, name: view.name, builtIn: true, starred: stars.get(view.key) ?? view.starred === true }));
  const all = builtIn.filter((entry) => entry.id === ALL_VIEW_ID);
  const rest = builtIn.filter((entry) => entry.id !== ALL_VIEW_ID);
  const mine = own.map((view): SwitcherEntry => ({ id: view.id, name: view.name, builtIn: false, starred: view.isFavourite === true }));
  const starredMine = mine.filter((entry) => entry.starred).sort((a, b) => a.name.localeCompare(b.name));
  return [...all, ...rest.filter((entry) => entry.starred), ...starredMine, ...rest.filter((entry) => !entry.starred), ...mine.filter((entry) => !entry.starred)];
}

/** 🔒 The quick views: the starred entries in the switcher's order (decision 2880). */
export function quickViews(entries: readonly SwitcherEntry[]): SwitcherEntry[] {
  return entries.filter((entry) => entry.starred).slice(0, QUICK_VIEW_LIMIT);
}

/** 🔒 A sixth star on one list is refused (decision 2880); an unstar is never refused. */
export function starRefused(entries: readonly SwitcherEntry[], id: string): boolean {
  const entry = entries.find((e) => e.id === id);
  if (!entry || entry.starred) return false;
  return entries.filter((e) => e.starred).length >= QUICK_VIEW_LIMIT;
}

/**
 * 🔒 How many quick views the bar draws (decision 2880): the first three always,
 * and the fourth and fifth only where each one's right edge, measured in one
 * unwrapped row, falls inside the bar's width. A bar not yet measured draws them all.
 */
export function quickViewsShown(rightEdges: readonly number[], width: number | null): number {
  const count = Math.min(rightEdges.length, QUICK_VIEW_LIMIT);
  if (width === null) return count;
  const always = Math.min(count, QUICK_VIEWS_ALWAYS);
  let shown = always;
  while (shown < count && (rightEdges[shown] ?? Infinity) <= width) shown += 1;
  return shown;
}
